import React from "react";

const STYLES = `
  .sxi_7b41c9e2_main_container,
  .sxi_7b41c9e2_main_container * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .sxi_7b41c9e2_main_container {
    --sxi_7b41c9e2_primary: #A31A13;
    --sxi_7b41c9e2_accent: #E3530F;
    /* navy + slate sampled from the source artwork; not defined in branding.json */
    --sxi_7b41c9e2_navy: #16305F;
    --sxi_7b41c9e2_slate: #333;
    --sxi_7b41c9e2_muted: #333333;
    --sxi_7b41c9e2_hairline: #E7E2DC;
    --sxi_7b41c9e2_card_border: #F4E7DA;
    --sxi_7b41c9e2_radius_card: 16px;
    --sxi_7b41c9e2_radius_pill: 50px;
    --sxi_7b41c9e2_font: 'Mulish', sans-serif;
    --sxi_7b41c9e2_gap: clamp(2rem, 5vw, 4.5rem);
    --sxi_7b41c9e2_badge: 54px;

    /* Phone.png is 1536x1024 with a transparent surround; the artwork itself
       occupies a 410x714 box at (224, 242). These tokens crop to that box so
       the mockup carries no dead space and needs no hand-set height. */
    --sxi_7b41c9e2_art_w: 1536;
    --sxi_7b41c9e2_art_h: 1024;
    --sxi_7b41c9e2_art_box_w: 410;
    --sxi_7b41c9e2_art_box_h: 714;

    position: relative;
    overflow: hidden;
    padding: clamp(3.5rem, 7vw, 5.5rem) 0 calc(clamp(3.5rem, 7vw, 5.5rem) + 10px);
    background: #FFFCFA;
    color: var(--sxi_7b41c9e2_slate);
    -webkit-font-smoothing: antialiased;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_container {
    max-width: 74rem;
    margin-inline: auto;
    padding-inline: clamp(1.25rem, 4vw, 2rem);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_header {
    max-width: 46rem;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_title {
    font-size: 28px;
    font-weight: 900;
    line-height: 50px;
    letter-spacing: -0.01em;
    text-wrap: balance;
  }

  @media screen and (max-width: 767px) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_title {
      font-size: 25px;
      line-height: 35px;
    }
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_title_top {
    display: block;
    color: var(--sxi_7b41c9e2_navy);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_title_brand {
    display: block;
    color: var(--sxi_7b41c9e2_accent);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_divider {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    margin: 1.375rem 0 1.125rem;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_line {
    width: 56px;
    height: 3px;
    border-radius: 999px;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_line_accent { background: var(--sxi_7b41c9e2_accent); }
  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_line_navy { background: var(--sxi_7b41c9e2_navy); }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--sxi_7b41c9e2_primary);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_lead {
    font-size: clamp(1.0625rem, 0.95rem + 0.6vw, 1.25rem);
    color: var(--sxi_7b41c9e2_muted);
  }

  /* ---------- layout ---------- */

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_layout {
    display: grid;
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1fr);
    align-items: center;
    gap: var(--sxi_7b41c9e2_gap);
    margin-top: 1rem;
  }

  /* ---------- phone artwork ---------- */

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_stage {
    position: relative;
    display: grid;
    place-items: center;
    padding: clamp(0.75rem, 1.5vw, 1.25rem) 0;
    isolation: isolate;
  }

  /* The artwork block is exactly as wide as the phone, and every decorative
     shape is positioned in phone-box units. Anchoring them to the phone
     rather than to the (variable width) stage keeps the composition
     identical at every viewport instead of drifting as the column narrows. */
  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_art {
    position: relative;
    /* 80% of the original 17.5rem; the height follows the asset ratio */
    width: min(100%, 14rem);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_deco {
    position: absolute;
    inset: 0;
    z-index: -1;
    isolation: isolate;
    pointer-events: none;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_blob {
    position: absolute;
    width: 137.14%;
    aspect-ratio: 1;
    left: -50.31%;
    top: 8.1%;
    border-radius: 50%;
    background: linear-gradient(180deg, #FFD7B1 0%, #FBA45C 100%);
    opacity: 0.9;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_arc {
    position: absolute;
    border: 1.5px solid rgba(227, 83, 15, 0.55);
    border-radius: 50%;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_arc_top {
    width: 83.88%;
    aspect-ratio: 1 / 1.25;
    left: 52.72%;
    top: 12.51%;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_arc_bottom {
    width: 113.08%;
    aspect-ratio: 1 / 0.3;
    left: -45.76%;
    top: 76.83%;
    transform: rotate(-8deg);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_dots {
    position: absolute;
    width: 77px;
    height: 61px;
    left: -54.87%;
    top: 19.12%;
    background-image: radial-gradient(var(--sxi_7b41c9e2_accent) 1.7px, transparent 1.8px);
    background-size: 15.2px 15.2px;
    opacity: 0.85;
  }

  /* Crop window onto the artwork: its ratio comes from the asset's own
     content box, so the height is derived rather than hand-set. */
  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_phone {
    position: relative;
    width: 100%;
    aspect-ratio: var(--sxi_7b41c9e2_art_box_w) / var(--sxi_7b41c9e2_art_box_h);
    overflow: hidden;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_phone_img {
    position: absolute;
    /* 1536 / 410 */
    width: 374.6341%;
    height: auto;
    /* -224 / 410 */
    left: -54.6341%;
    /* -242 / 714 */
    top: -33.8935%;
    display: block;
    pointer-events: none;
    user-select: none;
  }

  /* ---------- feature list ---------- */

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_features {
    list-style: none;
    display: flex;
    flex-direction: column;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature {
    display: flex;
    align-items: center;
    gap: clamp(1rem, 2vw, 1.75rem);
    padding-block: 1.5rem;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature + .sxi_7b41c9e2_feature {
    border-top: 1px solid var(--sxi_7b41c9e2_hairline);
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_icon {
    flex: 0 0 auto;
    width: var(--sxi_7b41c9e2_badge);
    height: var(--sxi_7b41c9e2_badge);
    display: grid;
    place-items: center;
    color: var(--sxi_7b41c9e2_accent);
    background: #FFFFFF;
    border: 2px solid var(--sxi_7b41c9e2_accent);
    border-radius: 50%;
    transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease;
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_icon svg {
    width: 36px;
    height: 36px;
    display: block;
  }

  @media (hover: hover) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature:hover .sxi_7b41c9e2_feature_icon {
      transform: translateY(-3px);
      background: var(--sxi_7b41c9e2_accent);
      color: #FFFFFF;
      box-shadow: 0 14px 26px -12px rgba(163, 26, 19, 0.55);
    }
  }

  .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_copy {
    color: var(--sxi_7b41c9e2_muted);
    font-size: 1.3125rem;
    line-height: 1.45;
    text-wrap: balance;
  }

  /* ---------- responsive ---------- */

  @media (max-width: 991.98px) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_layout {
      grid-template-columns: 1fr;
      max-width: 30rem;
      margin-inline: auto;
      row-gap: 2.5rem;
    }

    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_copy {
      font-size: 1.1875rem;
    }
  }

  /* The decorative group spans wider than the phone itself. Below these
     widths the column is too narrow to hold it, so the group tightens
     around the phone instead of bleeding past the section edges. */
  @media (max-width: 560px) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_deco { transform: scale(0.8); }
  }

  @media (max-width: 420px) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_deco { transform: scale(0.68); }

    /* below this width the side gutter is narrower than the dot grid, so it
       would sit on top of the phone rather than beside it */
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_dots { display: none; }

    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_icon svg {
      width: 34px;
      height: 34px;
    }

    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_copy { font-size: 1.0625rem; }
  }

  @media (max-width: 360px) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_deco { transform: scale(0.58); }
  }

  @media (prefers-reduced-motion: reduce) {
    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature_icon { transition: none; }

    .sxi_7b41c9e2_main_container .sxi_7b41c9e2_feature:hover .sxi_7b41c9e2_feature_icon { transform: none; }
  }
`;

const PHONE_IMAGE = "https://campaigns.icici.bank.in/testing/images/Phone.png";

const CardsIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    focusable="false"
  >
    <rect x="5" y="11" width="38" height="27" rx="5" />
    <path d="M5 19h24" />
    <path d="M43 22h-6.5a3.5 3.5 0 0 0 0 7H43" />
    <circle cx="36.6" cy="25.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const CardControlIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    focusable="false"
  >
    <rect x="4" y="10" width="35" height="24" rx="4" />
    <path d="M4 17h35" />
    <path d="M10 26h7" />
    <circle cx="35" cy="34" r="9.5" fill="#FFFFFF" stroke="none" />
    <circle cx="35" cy="34" r="6" />
    <circle cx="35" cy="34" r="2.2" />
    <path d="M35 26.4v-2.2M35 43.8v-2.2M42.6 34h2.2M25.2 34h2.2M40.4 28.6l1.5-1.5M28.1 40.9l-1.5 1.5M40.4 39.4l1.5 1.5M28.1 27.1l-1.5-1.5" />
  </svg>
);

const SmartLockIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    focusable="false"
  >
    <rect x="11" y="21" width="26" height="21" rx="4" />
    <path d="M17.5 21v-5a6.5 6.5 0 0 1 13 0v5" />
    <circle cx="24" cy="29.5" r="2.6" />
    <path d="M24 32.1v3.6" />
  </svg>
);

const FEATURES = [
  {
    id: "one-app",
    Icon: CardsIcon,
    copy: "Manage your loans, investments, and payments in one app",
  },
  {
    id: "card-controls",
    Icon: CardControlIcon,
    copy: "Control your Debit Card and Credit Card settings",
  },
  {
    id: "smartlock",
    Icon: SmartLockIcon,
    copy: "Secure banking with SmartLock",
  },
];

export default function DigitalBankingIMobile({ imageSrc = PHONE_IMAGE }) {
  return (
    <section className="sxi_7b41c9e2_main_container" aria-labelledby="sxi_7b41c9e2_title">
      <style>{STYLES}</style>

      <div className="sxi_7b41c9e2_container">
        <div className="sxi_7b41c9e2_header">
          <h2 className="sxi_7b41c9e2_title" id="sxi_7b41c9e2_title">
            <span className="sxi_7b41c9e2_title_brand">
              Digital Banking at your fingertips <br /> with iMobile
            </span>
          </h2>
        </div>

        <div className="sxi_7b41c9e2_layout">
          <div className="sxi_7b41c9e2_stage">
            <div className="sxi_7b41c9e2_art">
              <span className="sxi_7b41c9e2_deco" aria-hidden="true">
                <span className="sxi_7b41c9e2_blob" />
                <span className="sxi_7b41c9e2_arc sxi_7b41c9e2_arc_top" />
                <span className="sxi_7b41c9e2_arc sxi_7b41c9e2_arc_bottom" />
                <span className="sxi_7b41c9e2_dots" />
              </span>

              <div className="sxi_7b41c9e2_phone">
                <img
                  className="sxi_7b41c9e2_phone_img"
                  src={imageSrc}
                  alt="ICICI Bank iMobile app open on a smartphone"
                  width="1536"
                  height="1024"
                  decoding="async"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>

          <ul className="sxi_7b41c9e2_features">
            {FEATURES.map(({ id, Icon, copy }) => (
              <li className="sxi_7b41c9e2_feature" key={id}>
                <span className="sxi_7b41c9e2_feature_icon" aria-hidden="true">
                  <Icon />
                </span>
                <p className="sxi_7b41c9e2_feature_copy">{copy}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
